export const validators = {
  email: (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  },

  url: (url: string): boolean => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  },

  phone: (phone: string): boolean => {
    const phoneRegex = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{4,6}$/
    return phoneRegex.test(phone)
  },

  password: (password: string): { valid: boolean; errors: string[] } => {
    const errors: string[] = []
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long')
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter')
    }
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter')
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least one number')
    }
    if (!/[!@#$%^&*]/.test(password)) {
      errors.push('Password must contain at least one special character')
    }
    
    return {
      valid: errors.length === 0,
      errors,
    }
  },

  fileType: (file: File, allowedTypes: string[]): boolean => {
    return allowedTypes.includes(file.type)
  },

  fileSize: (file: File, maxSizeInMB: number): boolean => {
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024
    return file.size <= maxSizeInBytes
  },

  required: (value: any): boolean => {
    if (value === null || value === undefined) return false
    if (typeof value === 'string') return value.trim().length > 0
    if (Array.isArray(value)) return value.length > 0
    return true
  },

  minLength: (value: string, min: number): boolean => {
    return value.length >= min
  },

  maxLength: (value: string, max: number): boolean => {
    return value.length <= max
  },

  min: (value: number, min: number): boolean => {
    return value >= min
  },

  max: (value: number, max: number): boolean => {
    return value <= max
  },

  pattern: (value: string, pattern: RegExp): boolean => {
    return pattern.test(value)
  },

  date: (date: string): boolean => {
    return !isNaN(Date.parse(date))
  },

  future: (date: string | Date): boolean => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj > new Date()
  },

  past: (date: string | Date): boolean => {
    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj < new Date()
  },
}

export type ValidationRule = {
  validator: (value: any) => boolean
  message: string
}

export class FormValidator {
  private rules: Map<string, ValidationRule[]> = new Map()

  addRule(field: string, rule: ValidationRule): this {
    if (!this.rules.has(field)) {
      this.rules.set(field, [])
    }
    this.rules.get(field)!.push(rule)
    return this
  }

  validate(data: Record<string, any>): {
    valid: boolean
    errors: Record<string, string[]>
  } {
    const errors: Record<string, string[]> = {}
    
    for (const [field, rules] of this.rules) {
      const value = data[field]
      const fieldErrors: string[] = []
      
      for (const rule of rules) {
        if (!rule.validator(value)) {
          fieldErrors.push(rule.message)
        }
      }
      
      if (fieldErrors.length > 0) {
        errors[field] = fieldErrors
      }
    }
    
    return {
      valid: Object.keys(errors).length === 0,
      errors,
    }
  }
}

export const sanitize = {
  html: (html: string): string => {
    const div = document.createElement('div')
    div.textContent = html
    return div.innerHTML
  },

  filename: (filename: string): string => {
    return filename.replace(/[^a-z0-9_\-\.]/gi, '_')
  },

  trim: (value: string): string => {
    return value.trim()
  },

  number: (value: any): number | null => {
    const num = Number(value)
    return isNaN(num) ? null : num
  },
}